import { supabase } from './supabase';

export interface StockItem {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  category_id: string | null;
  supplier_id: string | null;
  unit: string;
  sku: string | null;
  price: number;
  is_sellable: boolean;
  acquisition_cost: number;
  tax_rate: number;
  quantity: number;
  reorder_point: number;
  max_quantity: number;
  created_at: string;
  updated_at: string;
  is_deleted?: boolean;
  category?: {
    id: string;
    name: string;
  };
}

interface StockTransaction {
  id: string;
  item_id: string;
  type: 'purchase' | 'sale' | 'adjustment';
  quantity: number;
  price: number;
  notes: string | null;
  created_at: string;
  created_by: string | null;
}

export interface StockHistoryEntry {
  id: string;
  item_id: string;
  type: 'purchase' | 'sale' | 'adjustment';
  quantity: number;
  price: number;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  item_name: string;
  item_description: string | null;
  item_unit: string;
  category_name: string | null;
  supplier_name: string | null;
  created_by_email: string | null;
  created_by_name?: string | null;
  transaction_type: string;
  quantity_change: number;
  is_item_deleted?: boolean;
}

export async function getStockItems(businessId: string) {
  const { data, error } = await supabase
    .from('stock_items')
    .select(`
      *,
      category:stock_categories(
        id,
        name
      ),
      stock_history:stock_transactions(
        id,
        type,
        quantity,
        price,
        created_at
      )
    `)
    .eq('business_id', businessId)
    .order('name');

  if (error) throw error;
  return data as (StockItem & { stock_history: StockTransaction[] })[];
}

async function getStockItem(id: string) {
  const { data, error } = await supabase
    .from('stock_items')
    .select(`
      *,
      category:stock_categories(
        id,
        name
      )
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as StockItem;
}

export async function createStockItem(item: Omit<StockItem, 'id' | 'created_at' | 'updated_at'>) {
  // First create the stock item with zero quantity
  const { data: stockItem, error: stockError } = await supabase
    .from('stock_items')
    .insert([{
      ...item,
      quantity: 0, // Always start with 0 quantity
      reorder_point: item.reorder_point || 0,
      max_quantity: item.max_quantity || 100
    }])
    .select()
    .single();

    if (stockError) {
      console.error('Error creating stock item:', stockError);
      throw new Error(stockError.message || 'Failed to create stock item');
    }

  // If initial quantity is specified, create a purchase transaction
  if (item.quantity > 0) {
    try {
      await createStockTransaction({
        item_id: stockItem.id,
        type: 'purchase',
        quantity: item.quantity,
        price: item.acquisition_cost,
        notes: 'Initial stock'
      });
    } catch (err) {
      // If transaction fails, delete the item
      await supabase
        .from('stock_items')
        .delete()
        .eq('id', stockItem.id);
      throw err;
    }
  }

  // Get the final item state with all relations
  const { data: createdItem, error: fetchError } = await supabase
    .from('stock_items')
    .select(`
      *,
      category:stock_categories(
        id,
        name
      )
    `)
    .eq('id', stockItem.id)
    .single();

  if (fetchError) throw fetchError;
  return createdItem as StockItem;
}

async function updateStockItem(id: string, updates: Partial<StockItem>) {
  const { data, error } = await supabase
    .from('stock_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as StockItem;
}

export async function deleteStockItem(id: string) {
  const { error } = await supabase
    .from('stock_items')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function createStockTransaction(transaction: Omit<StockTransaction, 'id' | 'created_at' | 'created_by'>) {
  // Validate transaction
  if (!transaction.item_id) {
    throw new Error('Item ID is required');
  }
  if (!transaction.type) {
    throw new Error('Transaction type is required');
  }
  if (!transaction.quantity || transaction.quantity <= 0) {
    throw new Error('Quantity must be greater than 0');
  }
  if (typeof transaction.price !== 'number' || transaction.price < 0) {
    throw new Error('Price cannot be negative');
  }

  try {
    // Create transaction record
    const { data: transactionData, error: transactionError } = await supabase
      .from('stock_transactions')
      .insert([{
        item_id: transaction.item_id,
        type: transaction.type,
        quantity: transaction.quantity,
        price: transaction.price,
        notes: transaction.notes || null
      }])
      .select()
      .single();

    if (transactionError) throw transactionError;
    return transactionData as StockTransaction;

  } catch (err) {
    console.error('Error processing stock transaction:', err);
    throw err instanceof Error ? err : new Error('Failed to process stock transaction');
  }
}

async function getStockTransactions(itemId: string) {
  const { data, error } = await supabase
    .from('stock_transactions')
    .select('*')
    .eq('item_id', itemId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as StockTransaction[];
}

export async function getStockHistory(businessId: string) {
  const { data, error } = await supabase
    .from('stock_history_with_details') 
    .select(`
      *,
      item:stock_items(
        category:stock_categories(
          id,
          name
        )
      )
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as StockHistoryEntry[];
}

export async function deleteStockHistoryEntry(id: string) {
  const { error: deleteError } = await supabase
    .from('stock_transactions')
    .delete()
    .eq('id', id);

  if (deleteError) throw deleteError;
}

async function getStockStats(businessId: string) {
  const { data: items, error } = await supabase
    .from('stock_items')
    .select('*')
    .eq('business_id', businessId);

  if (error) throw error;

  const stats = {
    totalItems: items.length,
    lowStock: items.filter(item => item.quantity <= item.reorder_point).length,
    totalValue: items.reduce((sum, item) => sum + (item.quantity * item.acquisition_cost), 0),
    averageValue: items.length > 0 
      ? items.reduce((sum, item) => sum + (item.quantity * item.acquisition_cost), 0) / items.length 
      : 0
  };

  return stats;
}